/**
 * hashUser.js — client-side SHA-256 hashing for Enhanced Conversions.
 *
 * Replaces the /api/hash-user route, which doesn't work on cPanel static hosts.
 * Uses the browser's built-in Web Crypto API — available in all modern browsers.
 *
 * Why client-side is safe here:
 *   - Hashing isn't a secret. Anyone can SHA-256 anything.
 *   - The value goes into Google Tag Manager dataLayer next anyway.
 *   - This just lets GTM send "hashed PII" instead of plain PII upstream.
 *
 * Usage:
 *   import { hashUser } from '@/lib/hashUser';
 *   const { hashed_email, hashed_phone } = await hashUser({ email, phone });
 */

async function sha256(value) {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  const bytes = new TextEncoder().encode(normalized);
  const hashBuffer = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Returns { hashed_email, hashed_phone } where each is a 64-char hex SHA-256
 * digest of the lowercased, trimmed value. Phone numbers are stripped of all
 * non-digits before hashing (matches the original /api/hash-user behavior).
 */
export async function hashUser({ email, phone }) {
  const [hashed_email, hashed_phone] = await Promise.all([
    email ? sha256(email) : null,
    phone ? sha256(phone.replace(/\D/g, '')) : null,
  ]);
  return { hashed_email, hashed_phone };
}
